import { AiDraftItem } from "@/pages/Private/pages/Messages/components/GenerateBar/types";

import { Conversation } from "@/pages/Private/pages/Apollo/schema/get-people";

import { SenderRecipientOption } from "../components/SenderRecipientDropdown";

import {
	Draft,
	DraftRecipient,
	DraftSender,
	createDefaultDraft,
	timingFromDays,
} from "./inboxDraft.types";
import {
	pickAiSenderOption,
	preferThreadSender,
	type AiSender,
} from "./resolveAiSenderUserId.util";
import { aiItemIsInvitation } from "./pendingInvitation.util";
import { resolveInboxChannel } from "./resolveInboxChannel.util";

import type { User } from "@/pages/Public/pages/Login/schema/login";

interface BuildReinjectDraftsParams {
	items: AiDraftItem[];
	senderOptions: SenderRecipientOption[];
	aiSender: AiSender;
	fallbackSender: SenderRecipientOption | undefined;
	recipientOption: SenderRecipientOption | undefined;
	user: User | null | undefined;
	startingDraftId: number;
	/** Senders whose own invitation to the lead is pending: their note is no invitation (PREC-4216). */
	pendingInvitations?: Map<number, string | null>;
}

/**
 * Map already channel-filtered AiDraftItem[] into Draft[] for the re-inject path
 * (sender-channel switch on a lead that already has AI drafts). Each item's sender
 * prefers the AI-chosen sender, then the channel's resolved sender, then a
 * "Sender" placeholder; the recipient uses the resolved recipient or a "Recipient"
 * placeholder. Subject / invite-note / tone-of-voice origin are copied through.
 *
 * Pure: does not mutate refs. The caller advances its own id counter from
 * `nextDraftId`.
 */
export const buildReinjectDrafts = ({
	items,
	senderOptions,
	aiSender,
	fallbackSender,
	recipientOption,
	user,
	startingDraftId,
	pendingInvitations,
}: BuildReinjectDraftsParams): { drafts: Draft[]; nextDraftId: number } => {
	let nextDraftId = startingDraftId;

	const drafts: Draft[] = items.map((item, idx) => {
		const aiPicked = pickAiSenderOption(senderOptions, item.channel, aiSender);
		const resolvedSender = preferThreadSender(aiPicked, fallbackSender) ?? fallbackSender;

		const fromSender: DraftSender = resolvedSender
			? {
					userId: resolvedSender.userId ?? user?.id ?? 0,
					type: item.channel,
					label: resolvedSender.label,
					emailId: resolvedSender.emailId,
					emailAddress: resolvedSender.emailAddress,
				}
			: {
					userId: user?.id ?? 0,
					type: item.channel,
					label: "Sender",
				};

		const toRecipient: DraftRecipient = recipientOption
			? {
					type: item.channel,
					label: recipientOption.label,
					value: recipientOption.value ?? "",
				}
			: {
					type: item.channel,
					label: "Recipient",
					value: "",
				};

		const draft = createDefaultDraft(
			nextDraftId++,
			fromSender,
			toRecipient,
			item.content,
			timingFromDays(item.scheduledInDays ?? (idx === 0 ? 0 : idx * 3))
		);

		if (item.subject) {
			draft.subject = item.subject;
		}

		// A sender whose own invitation is pending cannot send a second one (PREC-4216).
		if (aiItemIsInvitation(item, fromSender.userId, pendingInvitations)) {
			draft.isInviteNote = true;
		}

		if (item.aiOrigin) {
			draft.aiOrigin = item.aiOrigin;
		}

		return draft;
	});

	return { drafts, nextDraftId };
};

interface ResolveReinjectTargetParams {
	conversations: Conversation[];
	listPersonId: number;
	senderOptions: SenderRecipientOption[];
	recipientOptions: SenderRecipientOption[];
	aiItems: AiDraftItem[];
	/** The channel the drafts on screen were built for. */
	previousChannel: "email" | "linkedin" | undefined;
}

export interface ReinjectTarget {
	channel: "email" | "linkedin";
	sender: SenderRecipientOption | undefined;
	recipient: SenderRecipientOption | undefined;
	/** The stored AI items of that channel (may be none). */
	items: AiDraftItem[];
}

/**
 * Where the re-inject path takes the drafts (extracted from `useAddInboxChat`): the channel the
 * lead's conversation history resolves to with the current sender options, its sender and
 * recipient, and the stored AI items of that channel. Null while that channel is not known yet
 * or is the one the drafts already have.
 */
export const resolveReinjectTarget = ({
	conversations,
	listPersonId,
	senderOptions,
	recipientOptions,
	aiItems,
	previousChannel,
}: ResolveReinjectTargetParams): ReinjectTarget | null => {
	const { senderOption, recipientOption } = resolveInboxChannel(
		conversations,
		listPersonId,
		senderOptions,
		recipientOptions
	);
	const channel = senderOption?.type;

	if (!channel || channel === previousChannel) {
		return null;
	}

	return {
		channel,
		sender: senderOption,
		recipient: recipientOption,
		items: aiItems.filter((item) => item.channel === channel),
	};
};
